<script setup lang="ts">
import {
    faCheck,
    faClose,
    faMessageQuestion,
    faUserHeadset,
} from '@fortawesome/pro-regular-svg-icons';
import { TicketActivityStates } from '@providers/support/support.type';
import { type ISupport } from '@providers/support';
import { type StepperStep, type StepsStyle, type DividerStyle } from '#imports';

const { support } = defineProps<{
    support: ISupport;
}>();
const workflow = computed(() => support.currentWorkFlow);
const analyticsStore = useAnalyticsStore();
const loading = ref(false);

const terminate = async () => {
    const { isConfirmed } = await swal({
        icon: 'warning',
        text: 'از لغو درخواست اطمینان دارید؟',
        confirmButtonText: 'بله',
        showCancelButton: true,
        cancelButtonText: 'خیر',
    });
    if (isConfirmed) {
        await workflow.value?.terminate();
        analyticsStore.trackAll('support-cancel');
    }
};

const { $error, $success } = useNotify();

const showForm = ref(false);
const input = ref('');
const currentStep = computed(() => workflow.value?.currentStep);
const invalid = ref(false);

function validate() {
    invalid.value = !input.value.trim();
}

const steps = computed<StepperStep[]>(() => [
    {
        icon: faMessageQuestion,
    },
    {
        icon: faUserHeadset,
    },
    {
        icon: faCheck,
        hidden:
            currentStep.value?.ticketActivityState ===
            TicketActivityStates.Failed,
        styleOverride: {
            current: {
                backgroundColor: 'var(--green-500)',
                borderColor: 'var(--green-500)',
            },
        },
    },
    {
        icon: faClose,
        hidden:
            currentStep.value?.ticketActivityState !==
            TicketActivityStates.Failed,
        styleOverride: {
            current: {
                backgroundColor: 'var(--red-500)',
                borderColor: 'var(--red-500)',
            },
        },
    },
]);

const stepStyles: StepsStyle = {
    previous: {
        backgroundColor: 'var(--blue-400)',
        iconColor: 'white',
        borderColor: 'var(--blue-400)',
    },
    current: {
        backgroundColor: 'var(--blue-400)',
        iconColor: 'white',
        borderColor: 'var(--blue-400)',
    },
    next: {
        backgroundColor: 'transparent',
        iconColor: 'var(--cool-gray-600)',
        borderColor: 'var(--cool-gray-600)',
    },
};

const dividerStyles: DividerStyle = {
    activeColor: 'var(--blue-400)',
    inactiveColor: 'var(--cool-gray-300)',
};

const step = computed(() => {
    switch (currentStep.value?.ticketActivityState) {
        case TicketActivityStates.Requested:
            return 0;
        case TicketActivityStates.UnderReview:
            return 1;
        case TicketActivityStates.Succeed:
        case TicketActivityStates.Failed:
            return 2;
        default:
            return 0;
    }
});

function toggleForm() {
    showForm.value = !showForm.value;
    analyticsStore.trackAll('support-toggle-form', { value: showForm.value });
}

async function confirm() {
    try {
        if (showForm.value) {
            validate();
            if (invalid.value) {
                $error('موارد خواسته شده را تکمیل کنید.');
                return;
            }
            loading.value = true;
            await workflow.value?.terminate(input.value);
            $success('درخواست شما با موفقیت ثبت شد.');
        }
    } finally {
        loading.value = false;
    }
    analyticsStore.trackAll('support-success', {
        form: showForm.value,
        isFinal: workflow.value?.currentStep?.isFinal,
    });
    workflow.value?.stop();
    support.init();
}
onMounted(() => {
    analyticsStore.trackAll('support-final-activity');
});
</script>
<template>
    <div class="root">
        <div v-if="showForm" class="form">
            <mr-typography size="x-large" weight="500">
                متأسفانه درخواست شما قابل انجام نبود.
            </mr-typography>
            <mr-typography size="large">
                لطفاً در قسمت توضیحات، درخواست خود را شرح دهید. همکاران ما پس از
                بررسی، نتیجه را از طریق پیامک یا تماس اطلاع خواهند داد.
            </mr-typography>
            <mr-input
                v-model="input"
                full-width
                required
                :invalid
                text-area
                label="توضیحات"
                placeholder="لطفاً شرح درخواست خود را بنویسید."
                @update:model-value="validate"
            />
        </div>
        <div v-else class="ticket-activity">
            <div v-if="currentStep?.ticketActivityState" class="stepper">
                <mr-stepper
                    v-model="step"
                    :steps
                    :step-styles
                    :divider-styles
                />
            </div>
            <div class="support-content">
                <mr-typography
                    v-if="workflow?.currentStep?.activitySubTitle"
                    weight="500"
                    size="x-large"
                    align="center"
                    v-html="workflow?.currentStep?.activitySubTitle"
                />
                <mr-typography
                    v-if="workflow?.currentStep?.activityDescription"
                    color="var(--gray-800)"
                    v-html="workflow?.currentStep?.activityDescription"
                />
            </div>
        </div>
        <slot />
        <div class="spacer" />
        <div class="submit-wrapper">
            <mr-button
                v-if="
                    currentStep?.ticketActivityState ===
                    TicketActivityStates.Requested
                "
                variant="bordered"
                size="medium"
                full-width
                @click="terminate"
            >
                منصرف شدم
            </mr-button>
            <mr-button
                v-else-if="currentStep?.needFurtherReviewButton"
                variant="bordered"
                size="medium"
                full-width
                @click="toggleForm"
            >
                {{ showForm ? 'بازگشت' : 'درخواست بررسی بیشتر' }}
            </mr-button>
            <mr-button size="medium" :loading full-width @click="confirm">
                {{ showForm ? 'ثبت درخواست' : 'متوجه شدم' }}
            </mr-button>
        </div>
    </div>
</template>
<style scoped lang="scss">
.root {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    .form {
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-items: center;
    }
    .ticket-activity {
        display: flex;
        flex-direction: column;
        gap: 36px;
        align-items: center;
        .stepper {
            width: 264px;
            padding-top: 20px;
        }
        .support-content {
            display: flex;
            width: 100%;
            flex-direction: column;
            gap: 8px;
        }
    }
    .spacer {
        flex-grow: 1;
    }
    .submit-wrapper {
        display: flex;
        gap: 12px;
    }
}
</style>
